import { get } from 'lodash';

// Guards against multiple concurrent 401s (e.g. a batch of parallel uploads)
// each triggering their own logout + redirect within a single page load.
let isLoggingOut = false;

// The login page is where /api/logout redirects us back to. App fires an
// authenticated request (the access-rules fetch) on every mount, so it 401s
// here while logged out — forcing another logout would bounce us straight back
// through /api/logout forever. There's no session to end on the login page.
const isOnLoginPage = (): boolean => window.location.hash.startsWith('#/login');

export const clearAllCookies = (): void => {
  try {
    const { hostname } = window.location;
    // HttpOnly cookies (jwt, express:sess, CloudFront-*) are not visible here and
    // are cleared by the backend /api/logout redirect below. Expire everything JS
    // can see across the host and its parent domain + root path.
    const domainScopes =
      hostname === 'localhost' ? [''] : ['', `;domain=${hostname}`, `;domain=.${hostname}`];

    document.cookie.split(';').forEach((cookie) => {
      const name = cookie.split('=')[0].trim();

      if (!name) {
        return;
      }

      domainScopes.forEach((domain) => {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/${domain}`;
      });
    });
  } catch (e) {
    console.log(e);
  }
};

const clearCaches = async (): Promise<void> => {
  try {
    if ('caches' in window) {
      const keys = await window.caches.keys();
      await Promise.all(keys.map((key) => window.caches.delete(key)));
    }
  } catch (e) {
    console.log(e);
  }
};

/**
 * Hard logout used when the backend reports the session is no longer valid
 * (401 Unauthorized). Clears every client-side artifact we can reach, then
 * hands off to the backend logout endpoint to clear the HttpOnly cookies and
 * end the server session. Idempotent: only the first call does the work.
 */
export const forceLogoutOnUnauthorized = async (): Promise<void> => {
  if (isLoggingOut || isOnLoginPage()) return;

  isLoggingOut = true;

  if (window?.newrelic?.addPageAction) {
    window.newrelic.addPageAction('forceLogout', {
      reason: 'unauthorized_401',
      path: window.location.hash || window.location.pathname
    });
  }

  try {
    clearAllCookies();
    window.localStorage.clear();
    window.sessionStorage.clear();

    if (get(window, 'Intercom.booted')) {
      window.Intercom && window.Intercom('shutdown');
    }

    await clearCaches();
  } catch (e) {
    if (window?.newrelic?.noticeError) {
      window.newrelic.noticeError('forceLogoutClearError', e);
    }
  } finally {
    window.location.href = `${window.API_URL}/api/logout`;
  }
};
